import { z } from 'zod';
import { AccountOnboardingType } from '../enums';
import { CivilDateDto } from './civilDate';
import { CountriesEnum } from './countries';
import {
  FacebookUrlSchema,
  InstagramUrlSchema,
  LinkedInUrlSchema,
  TwitterUrlSchema,
  TikTokUrlSchema,
} from './socialMedia';

/**
 * ONBOARDING VALIDATION DTOs
 */

// ORGANIZATION VALIDATION - Contact and address are required, social media is optional
export const OrganizationOnboardingAddressValidationDto = z.object({
  country: CountriesEnum,
  street: z
    .string()
    .max(255, 'VALIDATION__Max255CharLimit')
    .min(3, 'VALIDATION__Min3CharLimit')
    .or(z.literal(''))
    .transform((e) => (e === '' ? undefined : e))
    .optional(),
  city: z
    .string()
    .min(3, 'VALIDATION__Min3CharLimit')
    .max(255, 'VALIDATION__Max255CharLimit')
    .transform((e) => (e === '' ? undefined : e)),
  zip: z
    .string()
    .min(3, 'VALIDATION__Min3CharLimit')
    .max(255, 'VALIDATION__Max255CharLimit')
    .transform((e) => (e === '' ? undefined : e)),
  isAddressPublic: z.boolean().optional(),
});

export const OrganizationOnboardingContactValidationDto = z.object({
  phoneNumber: z
    .string()
    .min(3, 'VALIDATION__Min3CharLimit')
    .max(255, 'VALIDATION__Max255CharLimit'),
  phoneCountryCode: CountriesEnum,
  isPhoneNumberPublic: z.boolean().optional(),
});

export const OrganizationOnboardingSocialMediaValidationDto = z
  .array(
    z
      .object({ type: z.string().optional(), url: z.string().optional() })
      .superRefine((item, ctx) => {
        if (item.type === 'FACEBOOK' && item.url) {
          const result = FacebookUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
        if (item.type === 'INSTAGRAM' && item.url) {
          const result = InstagramUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
        if (item.type === 'TWITTER' && item.url) {
          const result = TwitterUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
        if (item.type === 'LINKEDIN' && item.url) {
          const result = LinkedInUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
        if (item.type === 'TIKTOK' && item.url) {
          const result = TikTokUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
      })
      .optional(),
  )

  .optional();

export type ApiOrganizationOnboardingAddressValidationDto = z.infer<
  typeof OrganizationOnboardingAddressValidationDto
>;

export type ApiOrganizationOnboardingContactValidationDto = z.infer<
  typeof OrganizationOnboardingContactValidationDto
>;

export type ApiOrganizationOnboardingSocialMediaValidationDto = z.infer<
  typeof OrganizationOnboardingSocialMediaValidationDto
>;

// TRAINER VALIDATION - Contact is required, address and social media are optional

export const TrainerOnboardingAddressValidationDto = z.object({
  country: CountriesEnum.optional(),
  street: z
    .union([
      z.string().max(255, 'VALIDATION__Max255CharLimit'),
      z.string().length(0),
    ])
    .optional()
    .or(z.literal(''))
    .transform((e) => (e === '' ? undefined : e))
    .nullable(),
  city: z
    .union([
      z
        .string()
        .min(3, 'VALIDATION__Min3CharLimit')
        .max(255, 'VALIDATION__Max255CharLimit'),
      z.string().length(0),
    ])

    .optional()
    .transform((e) => (e === '' ? undefined : e))
    .nullable(),
  zip: z
    .union([
      z
        .string()
        .min(3, 'VALIDATION__Min3CharLimit')
        .max(255, 'VALIDATION__Max255CharLimit'),
      z.string().length(0),
    ])
    .optional()
    .transform((e) => (e === '' ? undefined : e))
    .nullable(),
  isAddressPublic: z.boolean().optional(),
});

export const TrainerOnboardingContactValidationDto = z.object({
  phoneNumber: z
    .string()
    .min(3, 'VALIDATION__Min3CharLimit')
    .max(255, 'VALIDATION__Max255CharLimit'),
  phoneCountryCode: CountriesEnum,
  isPhoneNumberPublic: z.boolean().optional(),
});

export const TrainerOnboardingSocialMediaValidationDto = z
  .array(
    z
      .object({ type: z.string().optional(), url: z.string().optional() })
      .superRefine((item, ctx) => {
        if (item.type === 'FACEBOOK' && item.url) {
          const result = FacebookUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
        if (item.type === 'INSTAGRAM' && item.url) {
          const result = InstagramUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
        if (item.type === 'TWITTER' && item.url) {
          const result = TwitterUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
        if (item.type === 'LINKEDIN' && item.url) {
          const result = LinkedInUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
        if (item.type === 'TIKTOK' && item.url) {
          const result = TikTokUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
      })
      .optional(),
  )
  .optional();

export type ApiTrainerOnboardingAddressValidationDto = z.infer<
  typeof TrainerOnboardingAddressValidationDto
>;

export type ApiTrainerOnboardingContactValidationDto = z.infer<
  typeof TrainerOnboardingContactValidationDto
>;

export type ApiTrainerOnboardingSocialMediaValidationDto = z.infer<
  typeof TrainerOnboardingSocialMediaValidationDto
>;

// CLIENT VALIDATION - Contact is required, address and social media are optional
export const ClientOnboardingAddressValidationDto = z
  .object({
    country: CountriesEnum.optional().or(z.string().optional().nullable()),
    street: z
      .string()
      .max(255, 'VALIDATION__Max255CharLimit')
      .nullable()
      .optional(),
    city: z
      .string()
      .max(255, 'VALIDATION__Max255CharLimit')
      .nullable()
      .optional(),
    zip: z
      .string()
      .max(255, 'VALIDATION__Max255CharLimit')
      .nullable()
      .optional(),
    isAddressPublic: z.boolean().optional(),
  })
  .optional();

export const ClientOnboardingSettingsValidationDto = z
  .object({
    dateOfBirth: CivilDateDto.optional(),
    timezone: z.string().optional(),
    locale: z.string().optional(),
  })
  .optional();

export const ClientOnboardingContactValidationDto = z.object({
  phoneNumber: z
    .string()
    .min(3, 'VALIDATION__Min3CharLimit')
    .max(255, 'VALIDATION__Max255CharLimit'),
  phoneCountryCode: CountriesEnum,
  isPhoneNumberPublic: z.boolean().optional(),
});

export const ClientOnboardingSocialMediaValidationDto = z
  .array(
    z
      .object({ type: z.string(), url: z.string().optional() })
      .superRefine((item, ctx) => {
        if (item.type === 'FACEBOOK' && item.url) {
          const result = FacebookUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
        if (item.type === 'INSTAGRAM' && item.url) {
          const result = InstagramUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
        if (item.type === 'TWITTER' && item.url) {
          const result = TwitterUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
        if (item.type === 'LINKEDIN' && item.url) {
          const result = LinkedInUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
        if (item.type === 'TIKTOK' && item.url) {
          const result = TikTokUrlSchema.safeParse(item.url);
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message, // Reuse the existing error message
                path: ['url'],
              });
            });
          }
        }
      })
      .optional(),
  )
  .optional();

export type ApiClientOnboardingAddressValidationDto = z.infer<
  typeof ClientOnboardingAddressValidationDto
>;

export type ApiClientOnboardingContactValidationDto = z.infer<
  typeof ClientOnboardingContactValidationDto
>;

export type ApiClientOnboardingSocialMediaValidationDto = z.infer<
  typeof ClientOnboardingSocialMediaValidationDto
>;

// ACTUAL ONBOARDING DTOs
// CLIENT
export const ClientOnboardingDto = z
  .object({
    name: z.string().optional(),
    email: z.string().trim().email().optional(),
    type: z.enum([AccountOnboardingType.CLIENT]),
    address: ClientOnboardingAddressValidationDto.optional(),
    contact: ClientOnboardingContactValidationDto,
    socialMedia: ClientOnboardingSocialMediaValidationDto.optional(),
    userSettings: ClientOnboardingSettingsValidationDto.optional(),
  })
  .strict();

export type ApiClientOnboardingDto = z.infer<typeof ClientOnboardingDto>;

// TRAINER
export const TrainerOnboardingDto = z
  .object({
    // Optional — provisioning falls back to the account's own name when this
    // is absent OR empty, so a whitespace-only value trims to that fallback
    // rather than becoming the organization's name (F6, bugz-5).
    name: z.string().trim().optional(),
    email: z.string().trim().email().optional(),
    type: z.enum([AccountOnboardingType.TRAINER]),
    address: TrainerOnboardingAddressValidationDto.optional(),
    contact: TrainerOnboardingContactValidationDto,
    socialMedia: TrainerOnboardingSocialMediaValidationDto.optional(),
    // MV2-564 — Activity tags picked at registration.
    tagIds: z.array(z.string().uuid()).optional(),
  })
  .strict();

export type ApiTrainerOnboardingDto = z.infer<typeof TrainerOnboardingDto>;

// ORGANIZATION
export const OrganizationOnboardingDto = z
  .object({
    type: z.enum([AccountOnboardingType.ORGANIZATION]),
    isPublic: z.boolean().optional(),
    // Trimmed before `.min(3)` — see UpdateOrganizationDto (F6, bugz-5).
    name: z
      .string()
      .trim()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit'),
    email: z.string().trim().email().optional(),
    address: OrganizationOnboardingAddressValidationDto,
    contact: OrganizationOnboardingContactValidationDto,
    socialMedia: OrganizationOnboardingSocialMediaValidationDto.optional(),
    // MV2-564 — Activity tags picked at registration.
    tagIds: z.array(z.string().uuid()).optional(),
  })
  .strict();

export type ApiOrganizationOnboardingDto = z.infer<
  typeof OrganizationOnboardingDto
>;

// MEMBER — trainer/admin waiting on an invitation to someone else's
// organization (F4). No payload beyond the type: no role, no org, nothing to
// validate — the shared onboarding tail just stamps onboardingCompletedAt.
export const MemberOnboardingDto = z
  .object({
    type: z.enum([AccountOnboardingType.MEMBER]),
  })
  .strict();

export type ApiMemberOnboardingDto = z.infer<typeof MemberOnboardingDto>;

// F7 — "Add profile": creating an additional organization/PT profile from
// inside the app (`POST auth/me/profiles`), not onboarding. Same payload
// shape as the corresponding onboarding step — reused verbatim (no
// copy-pasted schema) via a discriminated union on `type`.
export const AddProfileDto = z.discriminatedUnion('type', [
  OrganizationOnboardingDto,
  TrainerOnboardingDto,
]);

export type ApiAddProfileDto = z.infer<typeof AddProfileDto>;

// VALIDATION ONLY
export const OnboardingValidationOnly = z
  .object({
    type: z.enum([
      AccountOnboardingType.ORGANIZATION,
      AccountOnboardingType.CLIENT,
      AccountOnboardingType.TRAINER,
    ]),
    // Trimmed before `.min(3)` so step validation answers exactly like the
    // final onboarding DTO (F6, bugz-5).
    name: z
      .string()
      .trim()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    email: z.string().trim().email().optional(),
    address: OrganizationOnboardingAddressValidationDto.optional(),
    contact: OrganizationOnboardingContactValidationDto.optional(),
    socialMedia: OrganizationOnboardingSocialMediaValidationDto.optional(),
    userSettings: ClientOnboardingSettingsValidationDto.optional(),
    organizationId: z.string().uuid().optional(),
  })
  .strict();

export type ApiOnboardingValidationOnly = z.infer<
  typeof OnboardingValidationOnly
>;
